import { type CSSProperties } from "react";
import { Pill } from "../primitives";
import type { LibraryNode } from "../../../lib/types";
import { useMoveNode } from "../../hooks/use-move-node";

interface ReparentPreviewProps {
  open: boolean;
  agent: LibraryNode;
  oldParentPath: LibraryNode[];
  newParentPath: LibraryNode[];
  newParentId: string;
  onClose: () => void;
  onConfirmed?: () => void;
}

function pathToNames(nodes: LibraryNode[]): string[] {
  return nodes.map((n) => n.name);
}

export function ReparentPreview({
  open,
  agent,
  oldParentPath,
  newParentPath,
  newParentId,
  onClose,
  onConfirmed,
}: ReparentPreviewProps) {
  const moveNode = useMoveNode();

  if (!open) return null;

  const oldNames = new Set(pathToNames(oldParentPath));
  const newNames = new Set(pathToNames(newParentPath));

  const gained = newParentPath.filter((n) => !oldNames.has(n.name));
  const lost = oldParentPath.filter((n) => !newNames.has(n.name));

  const gainedInstructions = gained.reduce((acc, n) => acc + (n.instructions?.split("\n").length ?? 0), 0);
  const lostInstructions = lost.reduce((acc, n) => acc + (n.instructions?.split("\n").length ?? 0), 0);

  function handleConfirm() {
    moveNode.mutate(
      { id: agent.id, new_parent_id: newParentId },
      {
        onSuccess: () => {
          onConfirmed?.();
          onClose();
        },
      },
    );
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 520,
    maxWidth: "90vw",
  };

  const column: CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  const columnHeader: CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    color: "var(--color-text-tertiary)",
    marginBottom: 8,
  };

  const chipList: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal} role="dialog" aria-label="Confirm reparent">
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4, color: "var(--color-text-primary)" }}>
          Move {agent.name}
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 20 }}>
          Review the inherited context changes before confirming.
        </div>

        <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
          <div style={column}>
            <div style={columnHeader}>Currently inherits from</div>
            <div style={chipList}>
              {oldParentPath.map((n) => {
                const isLost = lost.some((l) => l.id === n.id);
                return (
                  <div key={n.id} style={{ opacity: isLost ? 0.5 : 1 }}>
                    <Pill variant={isLost ? "red" : "gray"}>
                      {n.name}
                      {n.instructions ? ` · ${n.instructions.split("\n").length}` : ""}
                    </Pill>
                  </div>
                );
              })}
              {oldParentPath.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>None</div>
              )}
            </div>
          </div>

          <div style={column}>
            <div style={columnHeader}>Will inherit from</div>
            <div style={chipList}>
              {newParentPath.map((n) => {
                const isGained = gained.some((g) => g.id === n.id);
                return (
                  <div key={n.id}>
                    <Pill variant={isGained ? "green" : "gray"}>
                      {n.name}
                      {n.instructions ? ` · ${n.instructions.split("\n").length}` : ""}
                    </Pill>
                  </div>
                );
              })}
              {newParentPath.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>None</div>
              )}
            </div>
          </div>
        </div>

        {(gainedInstructions > 0 || lostInstructions > 0) && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 16 }}>
            {lostInstructions > 0 && (
              <span>
                Loses {lostInstructions} {lostInstructions === 1 ? "instruction" : "instructions"} from{" "}
                {lost.map((n) => n.name).join(", ")}
              </span>
            )}
            {lostInstructions > 0 && gainedInstructions > 0 && " · "}
            {gainedInstructions > 0 && (
              <span>
                Gains {gainedInstructions} from {gained.map((n) => n.name).join(", ")}
              </span>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-variant="primary"
            onClick={handleConfirm}
            disabled={moveNode.isPending}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            {moveNode.isPending ? "Moving..." : "Confirm move"}
          </button>
        </div>
      </div>
    </div>
  );
}
